import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useParams } from 'react-router-dom';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Typography from '@mui/material/Typography';
import PageLayout from '../layout/PageLayout';
import DataTable from '../ui/DataTable';
import StatusChip from '../ui/StatusChip';
import { notify } from '../feedback/notify';
import { MascaraCnpj } from '../../utils/MascaraCnpj';
import { statusImportacao } from './ImportacoesListPage';

const formatarDataHora = (valor) => {
    if (!valor) return '—';
    const data = new Date(valor);
    return Number.isNaN(data.getTime()) ? valor : data.toLocaleString('pt-BR');
};

export const colunaCliente = {
    key: 'cliente',
    label: 'Cliente',
    minWidth: 220,
    render: (item) => (
        <Box>
            <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                {item?.cliente?.cnpj ? MascaraCnpj(item.cliente.cnpj) : 'CNPJ não identificado'}
            </Typography>
            <Typography variant="caption" color="text.secondary">{item?.cliente?.razaoSocial || '—'}</Typography>
        </Box>
    ),
};

export const colunaProcessado = {
    key: 'processado',
    label: 'Processado',
    render: (item) => <StatusChip label={item?.erro === null ? 'Sim' : 'Não'} tone={item?.erro === null ? 'success' : 'error'} />,
};

export const colunaErros = {
    key: 'erros',
    label: 'Erros',
    minWidth: 200,
    wrap: true,
    render: (item) => (
        <Typography variant="body2" sx={{ color: item?.detalhesErro ? 'error.main' : 'text.secondary' }}>
            {item?.detalhesErro || '—'}
        </Typography>
    ),
};

export const colunaStatusDetalhe = {
    key: 'status',
    label: 'Status',
    render: (item) => (
        <StatusChip
            label={item?.status || '—'}
            tone={item?.status === 'PROCESSADO' ? 'success' : 'error'}
        />
    ),
};

/** Detalhes de uma importação: resumo + tabela de itens. */
export default function DetalhesImportacaoPage({ endpoint, breadcrumbs, columns }) {
    const { idImportacao } = useParams();

    const [importacaoDetails, setImportacaoDetails] = useState([]);
    const [importacaoRealizada, setImportacaoRealizada] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const getImportacaoDetails = async () => {
            const token = localStorage.getItem('token');
            if (!idImportacao || !token) {
                setLoading(false);
                return;
            }
            try {
                const response = await axios.get(`${process.env.REACT_APP_API_URL}/${endpoint}/${idImportacao}`, {
                    headers: {
                        Authorization: token
                    }
                });
                setImportacaoDetails(response.data.importacao?.detalhes || []);
                setImportacaoRealizada(response.data.importacao);
            } catch (error) {
                console.error('Erro ao buscar detalhes da importação:', error);
                notify.error('Não foi possível carregar os detalhes da importação.');
            } finally {
                setLoading(false);
            }
        }

        getImportacaoDetails();
    }, [idImportacao, endpoint]);

    const total = importacaoDetails.length;
    const comErro = importacaoDetails.filter((d) => d?.erro !== null && d?.erro !== undefined).length;
    const status = statusImportacao(importacaoRealizada?.status);

    const resumo = [
        { label: 'Itens', value: importacaoRealizada?.totalArquivos ?? total },
        { label: 'Processados', value: importacaoRealizada?.processados ?? total - comErro },
        { label: 'Com erro', value: importacaoRealizada?.erros ?? comErro, danger: Number(importacaoRealizada?.erros ?? comErro) > 0 },
    ];

    return (
        <PageLayout
            title={`Importação #${idImportacao}`}
            subtitle={importacaoRealizada?.criadoEm ? `Realizada em ${formatarDataHora(importacaoRealizada.criadoEm)}` : 'Detalhes da importação'}
            breadcrumbs={breadcrumbs}
            actions={importacaoRealizada?.status ? <StatusChip label={status.label} tone={status.tone} /> : null}
        >
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' }, gap: 2, mb: 2 }}>
                {resumo.map((item) => (
                    <Card key={item.label} sx={{ px: 2.5, py: 2 }}>
                        <Typography variant="body2" color="text.secondary">{item.label}</Typography>
                        <Typography sx={{ fontSize: '1.5rem', fontWeight: 600, mt: 0.5, color: item.danger ? 'error.main' : 'text.primary' }}>
                            {loading ? '—' : item.value}
                        </Typography>
                    </Card>
                ))}
            </Box>

            <DataTable
                columns={columns}
                rows={importacaoDetails}
                getRowKey={(item, i) => `${item?.cliente?.cnpj ?? 'sem-cnpj'}-${item?.competencia ?? ''}-${i}`}
                loading={loading}
                emptyMessage="Nenhum item encontrado nesta importação."
            />
        </PageLayout>
    );
}
